import { useEffect, useRef, useState } from 'react';
import { api, UserVideoRecord } from '../lib/api';
import { MediaItem } from '../types/video';

export const useUserVideos = (opts?: { enabled?: boolean; pollIntervalMs?: number; includeArchived?: boolean }) => {
    const [videos, setVideos] = useState<MediaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [pollState, setPollState] = useState<'idle' | 'ok' | 'waiting' | 'retrying' | 'error'>('idle');
    const inFlightRef = useRef(false);
    const timeoutStreakRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        let timerId: number | null = null;
        const enabled = opts?.enabled ?? true;
        const basePollIntervalMs = Math.max(3000, Number(opts?.pollIntervalMs ?? 5000) || 5000);
        const scheduleNext = (delay: number) => {
            if (cancelled) return;
            timerId = window.setTimeout(() => {
                void fetchVideos();
            }, delay);
        };
        const fetchVideos = async () => {
            if (!enabled || cancelled) return;
            if (inFlightRef.current) {
                scheduleNext(basePollIntervalMs);
                return;
            }
            inFlightRef.current = true;
            let controlledWait = false;
            try {
                const response = await api.getUserVideoRecords(opts?.includeArchived === true);
                if (response.success && response.data?.rows) {
                    const normalized = response.data.rows
                        .map((row: UserVideoRecord): MediaItem | null => {
                            const url = typeof row.url === 'string' && row.url.trim().length > 0
                                ? row.url.trim()
                                : (typeof row.result_url === 'string' && row.result_url.trim().length > 0 ? row.result_url.trim() : null);
                            const mediaAvailable = row.media_available !== false;
                            if (!url && mediaAvailable) return null;
                            const createdAt = row.createdAt || row.created_at || null;
                            const createdDate = createdAt ? new Date(createdAt) : null;
                            const dateLabel = createdDate && !Number.isNaN(createdDate.getTime())
                                ? createdDate.toLocaleDateString()
                                : 'Unknown date';
                            const inferredType = normalizeMediaType(row.media_type) ?? inferMediaType(row.type, url || '');
                            return {
                                id: row.id || row._id || crypto.randomUUID(),
                                url: url || '',
                                thumbnail: row.thumbnail || row.thumbnail_url || url || undefined,
                                createdAt,
                                title: row.title || row.name || `Media ${dateLabel}`,
                                mediaType: inferredType,
                                jobId: row.job_id,
                                mediaAvailable,
                                unavailableReason: row.unavailable_reason || null,
                                archived: row.archived === true || mediaAvailable === false
                            };
                        })
                        .filter((item): item is MediaItem => item !== null);
                    setVideos(normalized);
                    setError(null);
                    setPollState('ok');
                    timeoutStreakRef.current = 0;
                } else if (response.requestStatus === 'timeout' || response.requestStatus === 'aborted') {
                    controlledWait = true;
                    timeoutStreakRef.current += 1;
                    setError(null);
                    setPollState(timeoutStreakRef.current > 1 ? 'retrying' : 'waiting');
                    console.debug('[ChopShop Media Poll]', {
                        state: response.requestStatus,
                        streak: timeoutStreakRef.current,
                    });
                } else {
                    setPollState('error');
                    setError(new Error(response.error || 'Failed to fetch media'));
                }
            } catch (err) {
                setPollState('error');
                setError(err as Error);
            } finally {
                inFlightRef.current = false;
                if (!cancelled) {
                    setLoading(false);
                    const backoffMultiplier = controlledWait ? Math.min(2 ** Math.min(timeoutStreakRef.current, 4), 8) : 1;
                    const nextDelay = Math.min(basePollIntervalMs * backoffMultiplier, 30000);
                    scheduleNext(nextDelay);
                }
            }
        };

        if (!enabled) {
            setLoading(false);
            setPollState('idle');
            return;
        }

        void fetchVideos();
        const onFocus = () => {
            if (!cancelled) {
                void fetchVideos();
            }
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !cancelled) {
                void fetchVideos();
            }
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            if (timerId !== null) window.clearTimeout(timerId);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [opts?.enabled, opts?.pollIntervalMs, opts?.includeArchived]);

    return { videos, loading, error, pollState };
};

function inferMediaType(type: string | undefined, url: string): MediaItem['mediaType'] {
    const t = String(type || '').toLowerCase();
    const u = String(url || '').toLowerCase();
    if (u.endsWith('.mp4') || u.includes('.mp4') || t.includes('vid') || t.includes('video')) return 'video';
    if (u.endsWith('.mp3') || u.endsWith('.wav') || u.endsWith('.m4a') || t.includes('tts') || t.includes('audio') || t.includes('voice')) return 'audio';
    return 'image';
}

function normalizeMediaType(value: string | undefined): MediaItem['mediaType'] | null {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'video' || normalized === 'image' || normalized === 'audio' || normalized === 'voice' || normalized === 'media') {
        return normalized as MediaItem['mediaType'];
    }
    return null;
}
